import type {FormulaResult} from '../../../../components/dice';
import type {RollMode} from './conditions';
import {formatSigned} from './format-number';

export type DiceTerm = {
	count: number;
	sides: number;
	/** оставить одну лучшую или худшую кость: преимущество и помеха */
	keep?: 'highest' | 'lowest';
};

export type DiceFormula = {
	dice: DiceTerm[];
	bonus: number;
};

const DICE_TERM = /^(\d*)d(\d+)$/i;

export const D20: DiceFormula = {dice: [{count: 1, sides: 20}], bonus: 0};

export const dieFormula = (sides: number, bonus = 0): DiceFormula => ({dice: [{count: 1, sides}], bonus});

export const isD20Test = ({dice}: DiceFormula) => dice.length === 1 && dice[0].sides === 20 && dice[0].count === 1;

export function withRollMode(formula: DiceFormula, mode: RollMode): DiceFormula {
	if (mode === 'normal' || !isD20Test(formula)) return formula;
	return {...formula, dice: [{count: 2, sides: 20, keep: mode === 'advantage' ? 'highest' : 'lowest'}]};
}

export function parseDice(text: string, bonus = 0): DiceFormula {
	const formula: DiceFormula = {dice: [], bonus};
	for (const rawTerm of text
		.replace(/\s+/g, '')
		.replace(/−/g, '-')
		.split(/(?=[+-])/)) {
		const sign = rawTerm.startsWith('-') ? -1 : 1;
		const term = rawTerm.replace(/^[+-]/, '');
		const dice = term.match(DICE_TERM);
		if (dice) formula.dice.push({count: Number(dice[1] || 1), sides: Number(dice[2])});
		else if (term) formula.bonus += sign * (Number(term) || 0);
	}
	return formula;
}

export const withBonus = (formula: DiceFormula, bonus: number): DiceFormula => ({
	...formula,
	bonus: formula.bonus + bonus,
});

export function formatDice({dice, bonus}: DiceFormula): string {
	const diceText = dice
		.map(({count, sides, keep}) => `${count}d${sides}${keep ? (keep === 'highest' ? 'kh1' : 'kl1') : ''}`)
		.join(' + ');
	if (!diceText) return String(bonus);
	return bonus ? `${diceText} ${formatSigned(bonus)}` : diceText;
}

export function formatRollResult({groups, modifier}: FormulaResult): string {
	const diceText = groups
		.map(({term, values}) => {
			const keep = term.keep ? (term.keep.highest ? ' лучшая' : ' худшая') : '';
			return `${term.count}d${term.sides}${keep} (${values.join(', ')})`;
		})
		.join(' + ');
	return modifier ? `${diceText} ${formatSigned(modifier)}` : diceText;
}

/** кость ресурса из таблицы класса: «D6» → «1d6» */
export const normalizeDie = (die: string) => die.trim().toLowerCase().replace(/^d/, '1d');
